import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { storageController } from '../controllers/StorageController';
import { resolveInitialLanguage } from './resolveInitialLanguage';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, isSupportedLanguage, type LanguageCode } from './types';

import commonEn from './locales/en/common';
import wizardEn from './locales/en/wizard';
import validationEn from './locales/en/validation';
import terminalEn from './locales/en/terminal';

import commonEs from './locales/es/common';
import wizardEs from './locales/es/wizard';
import validationEs from './locales/es/validation';
import terminalEs from './locales/es/terminal';

// A minimal custom i18next-browser-languagedetector plugin. Detection and
// caching are delegated to resolveInitialLanguage() and StorageController so
// the precedence (stored preference -> browser language -> default) lives in
// one unit-tested place, rather than being trusted to the plugin's own logic.
const getBrowserLanguages = (): readonly string[] => {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages ?? [navigator.language];
};

const storagePreferenceDetector = {
  name: 'storagePreference',
  lookup(): LanguageCode {
    return resolveInitialLanguage({
      storedPreference: storageController.getPreferredLanguage(),
      browserLanguages: getBrowserLanguages(),
      supportedLocales: SUPPORTED_LANGUAGES,
    });
  },
  cacheUserLanguage(language: string): void {
    if (isSupportedLanguage(language)) {
      storageController.setPreferredLanguage(language);
    }
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(storagePreferenceDetector);

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: commonEn,
        wizard: wizardEn,
        validation: validationEn,
        terminal: terminalEn,
      },
      es: {
        common: commonEs,
        wizard: wizardEs,
        validation: validationEs,
        terminal: terminalEs,
      },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['common', 'wizard', 'validation', 'terminal'],
    defaultNS: 'common',
    detection: {
      order: ['storagePreference'],
      caches: [],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
